import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import PageHeader from "../../Component/PageHeader";
import StatStrip from "../../Component/StatStrip";
import PeriodFilter, { periodRange } from "../../Component/PeriodFilter";
import Pill from "../../Component/Pill";
import RowMenu, { RowMenuItem } from "../../Component/RowMenu";
import Loader from "../../Component/Loader/Loader";
import InvoiceDrawer from "./components/InvoiceDrawer";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import { SELLER } from "../../utils/staticData";
import {
  ApiInvoice, customerName, inr2, inrShort, invoiceNo, invoiceStatus, invoiceTotals, planTypeInfo,
} from "./invoiceUtils";

// Interface for Stats (value + growth % vs previous period)
interface StatItem {
  value: number;
  growth: number;
}

interface InvoiceStats {
  invoiced: StatItem;
  plans: StatItem;
  walletTopups: StatItem;
  count: StatItem;
  unpaid: StatItem;
}

const EMPTY_STAT: StatItem = { value: 0, growth: 0 };

// Chips come from the invoice_plan_type values the API returns; this is just the display order
const TYPE_ORDER = ["monthly", "quarterly", "yearly", "annually", "wallet_topup", "wallet_deduction"];

const Invoices = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoiceList, setInvoiceList] = useState<ApiInvoice[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);

  // Stats State
  const [stats, setStats] = useState<InvoiceStats>({
    invoiced: EMPTY_STAT,
    plans: EMPTY_STAT,
    walletTopups: EMPTY_STAT,
    count: EMPTY_STAT,
    unpaid: EMPTY_STAT,
  });
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});
  const [methods, setMethods] = useState<string[]>([]);

  // Filters
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [planType, setPlanType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const [openInvoice, setOpenInvoice] = useState<ApiInvoice | null>(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; invoice: ApiInvoice } | null>(null);

  // /invoices?open=<invoice no> (links from the wallet ledger) → search for it
  useEffect(() => {
    const no = searchParams.get("open");
    if (no) {
      setSearchText(no);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch Invoice List (list + stats follow the selected period)
  const getInvoiceList = useCallback(async () => {
    // All time = no dates; custom waits until both dates are picked
    const [from, to] = periodRange(period, customFrom, customTo);
    if (period === "custom" && (!from || !to)) return;
    const startDate = from ? from.format('YYYY-MM-DD') : "";
    const endDate = to ? to.format('YYYY-MM-DD') : "";
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/subscription/invoices?page=${page}&limit=${pageSize}&planType=${encodeURIComponent(planType)}&invoiceStatus=${statusFilter}&method=${encodeURIComponent(methodFilter)}&search=${encodeURIComponent(searchQuery)}&sort=${sortOrder}&startDate=${startDate}&endDate=${endDate}`
      );

      if (response.status === 'success') {
        setInvoiceList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalInvoices(response.pagination.total);
        if (response.stats) setStats(response.stats);
        if (response.typeCounts) setTypeCounts(response.typeCounts);
        if (response.methods) setMethods(response.methods);
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading invoices!");
    } finally {
      setIsLoader(false);
    }
  }, [page, pageSize, planType, statusFilter, methodFilter, searchQuery, sortOrder, period, customFrom, customTo]);

  useEffect(() => {
    getInvoiceList();
  }, [getInvoiceList]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  // Export the rows currently loaded in the table as CSV
  const exportCsv = () => {
    if (!invoiceList.length) return toast.warning("Nothing to export");
    const head = ["Invoice", "Date", "Customer", "Customer ID", "GSTIN", "State", "Type", "Plan", "Taxable", "CGST", "SGST", "IGST", "Total", "Status", "Method"];
    const body = invoiceList.map(i => {
      const t = invoiceTotals(i);
      return [
        invoiceNo(i), i.createdAt ? moment(i.createdAt).format("DD MMM YYYY") : "", customerName(i), i.invoice_user_id?.user_unique_id || "",
        i.invoice_bill_gst_no || "", i.invoice_bill_state || "", planTypeInfo(i.invoice_plan_type)[0], i.invoice_plan_name || "",
        t.sub.toFixed(2), t.cgst.toFixed(2), t.sgst.toFixed(2), t.igst.toFixed(2), t.tot.toFixed(2),
        invoiceStatus(i)[1], i.invoice_transaction_id?.method || "",
      ];
    });
    const csv = [head, ...body].map(r => r.map(x => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `invoices-${planType}-${moment().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${invoiceList.length} invoices`);
  };

  const changeType = (k: string) => { setPlanType(k); setPage(1); };

  const resetFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setMethodFilter("all");
    setSortOrder("newest");
    changeType("all");
  };

  const menuItems = (i: ApiInvoice): RowMenuItem[] => {
    const items: RowMenuItem[] = [
      { icon: "ri-eye-line", label: "View invoice", onClick: () => setOpenInvoice(i) },
      { icon: "ri-download-2-line", label: "Download PDF", onClick: () => { setAutoPrint(true); setOpenInvoice(i); } },
    ];
    if (i.invoice_user_id?._id) {
      items.push("divider");
      items.push({ icon: "ri-building-2-line", label: "View customer", onClick: () => navigate(`/customers-and-users/view-user/${i.invoice_user_id!._id}`) });
    }
    return items;
  };

  const closeMenu = useCallback(() => setMenu(null), []);
  const onPrinted = useCallback(() => setAutoPrint(false), []);

  // Page numbers with gaps (1 … 4 5 6 … 20)
  const pageItems = (): (number | "gap")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out: (number | "gap")[] = [1];
    if (page > 3) out.push("gap");
    for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) out.push(p);
    if (page < totalPages - 2) out.push("gap");
    out.push(totalPages);
    return out;
  };

  // All + every invoice_plan_type found in the period (known types first)
  const typeKeys = Object.keys(typeCounts).sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a), ib = TYPE_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const allCount = Object.values(typeCounts).reduce((s, n) => s + n, 0);
  // keep the selected chip visible even when the new period has none of it
  if (planType !== "all" && !typeKeys.includes(planType)) typeKeys.push(planType);

  const firstRow = totalInvoices ? (page - 1) * pageSize + 1 : 0;
  const lastRow = Math.min(page * pageSize, totalInvoices);
  const delta = (s: StatItem) => (period === "all" ? "All time" : s.growth);

  return (
    <div className="container-fluid">
      {isLoader && <Loader />}
      <div className="cx">
        <PageHeader
          crumb="Invoices"
          title="Invoices & billing"
          sub="GST tax invoice for every plan purchase, renewal and wallet top-up."
          actions={<>
            <PeriodFilter
              allTime
              value={period}
              onChange={(p) => { setPeriod(p); setPage(1); }}
              from={customFrom}
              to={customTo}
              onRangeChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
            />
            <button type="button" className="cx-btn cx-btn-light" onClick={exportCsv}><i className="ri-download-2-line"></i>Export</button>
          </>}
        />

        {/* All time has no previous period, so no growth % */}
        <StatStrip items={[
          { label: "Total invoiced", value: inrShort(stats.invoiced.value), icon: "ri-file-list-3-line", delta: delta(stats.invoiced) },
          { label: "Plans", value: inrShort(stats.plans.value), icon: "ri-price-tag-3-line", delta: delta(stats.plans) },
          { label: "Wallet top-ups", value: inrShort(stats.walletTopups.value), icon: "ri-wallet-3-line", delta: delta(stats.walletTopups) },
          { label: "Invoices", value: stats.count.value, icon: "ri-file-copy-2-line", delta: delta(stats.count) },
          { label: "Unpaid", value: stats.unpaid.value, icon: "ri-error-warning-line", delta: delta(stats.unpaid), invert: true },
        ]} />

        <div className="cx-card">
          <div className="cx-toolbar">
            <div className="cx-chips">
              <button type="button" className={`cx-chip ${planType === "all" ? "on" : ""}`} onClick={() => changeType("all")}>All <b>{allCount}</b></button>
              {typeKeys.map(k => (
                <button type="button" key={k} className={`cx-chip ${planType === k ? "on" : ""}`} onClick={() => changeType(k)}>
                  {planTypeInfo(k)[0]} <b>{typeCounts[k] || 0}</b>
                </button>
              ))}
            </div>
            <div className="sp"></div>
            <input className="cx-input" type="search" placeholder="Invoice no / customer / email…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </div>
          <div className="cx-toolbar">
            <select className="cx-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">All status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
            <select className="cx-select" value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPage(1); }}>
              <option value="all">All payment methods</option>
              {methods.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
            <select className="cx-select" value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}>
              <option value="newest">Sort: Newest</option>
              <option value="oldest">Sort: Oldest</option>
            </select>
            <div className="sp"></div>
            <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
              <i className="ri-refresh-line"></i>Reset
            </button>
          </div>

          <div className="cx-tbl-wrap">
            <table className="cx-tbl">
              <thead>
                <tr><th>Invoice no.</th><th>Date</th><th>Customer</th><th>Type</th><th className="r">Amount</th><th>Status</th><th>Payment</th><th className="r">Actions</th></tr>
              </thead>
              <tbody>
                {invoiceList.length ? invoiceList.map(i => {
                  const t = invoiceTotals(i);
                  const [typeLabel, typeIcon] = planTypeInfo(i.invoice_plan_type);
                  const [tone, statusLabel] = invoiceStatus(i);
                  return (
                    <tr key={i._id} className="click" onClick={() => setOpenInvoice(i)}>
                      <td className="mono fw6">{invoiceNo(i)}</td>
                      <td className="fs12 muted">{i.createdAt ? moment(i.createdAt).format("DD MMM YYYY") : "—"}</td>
                      <td>
                        <b style={{ fontWeight: 600, fontSize: 13 }}>{customerName(i)}</b>
                        <div className="fs12 muted mono">{i.invoice_user_id?.user_unique_id ? `#${i.invoice_user_id.user_unique_id}` : i.invoice_bill_email || ""}</div>
                      </td>
                      <td>
                        <span className="cx-ity"><i className={typeIcon}></i>{typeLabel}</span>
                        <div className="fs12 muted">
                          {i.invoice_plan_name}
                          {i.invoice_type && i.invoice_type !== "standard" && <> · {i.invoice_type}</>}
                        </div>
                      </td>
                      <td className="r">
                        <div className="mono fw6">{inr2(t.tot)}</div>
                        <div className="fs12 muted mono">{inr2(t.sub)} + {inr2(t.gst)} GST</div>
                      </td>
                      <td><Pill tone={tone}>{statusLabel}</Pill></td>
                      <td className="fs12" style={{ textTransform: "uppercase" }}>{i.invoice_transaction_id?.method || <span className="muted">—</span>}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="cx-ra">
                          <button type="button" className="v" title="View invoice" onClick={() => setOpenInvoice(i)}><i className="ri-eye-line"></i></button>
                          <button type="button" title="More" onClick={(e) => setMenu({ anchor: e.currentTarget, invoice: i })}><i className="ri-more-2-fill"></i></button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={8}>
                      <div className="cx-empty">
                        <i className="ri-file-search-line"></i>
                        No invoices match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="cx-tfoot">
            <span>Showing <b>{firstRow}–{lastRow}</b> of <b>{(totalInvoices || 0).toLocaleString("en-IN")}</b> invoices</span>
            <span>Seller GSTIN <b className="mono">{SELLER.gstin}</b> · SAC <b className="mono">{SELLER.sac}</b></span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span>Rows</span>
              <select className="cx-select" value={pageSize} onChange={(e) => { setPageSize(+e.target.value); setPage(1); }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="cx-pager">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
                {pageItems().map((p, i) =>
                  p === "gap" ? (
                    <span key={`gap-${i}`} className="gap">…</span>
                  ) : (
                    <button type="button" key={p} className={p === page ? "on" : ""} onClick={() => setPage(p)}>{p}</button>
                  )
                )}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
              </div>
            </div>
          </div>
        </div>

        <RowMenu anchor={menu?.anchor || null} title={menu ? invoiceNo(menu.invoice) : undefined} items={menu ? menuItems(menu.invoice) : []} onClose={closeMenu} />
        <InvoiceDrawer
          invoice={openInvoice}
          autoPrint={autoPrint}
          onPrinted={onPrinted}
          onClose={() => { setOpenInvoice(null); setAutoPrint(false); }}
        />
      </div>
    </div>
  );
};

export default Invoices;
